import { Swiper, SwiperSlide } from 'swiper/react';
import React, {
    createContext,
    useContext,
    useEffect,
    useMemo,
    useState
} from 'react';

import { Button } from '$partials/ui/index';
import { ArrowRightIcon, ArrowLeftIcon } from '$partials/icon';


/* ========================================================================= */
/* Hook(s) */
/* ========================================================================= */

const CarouselContext = createContext();

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */

/**
 * @type {import('$types/components/partials/base.d.ts').Carousel}
 */

const Carousel = ({
    children,
    swiperConfig = {}
}) => {
    // State
    const [config, setConfig] = useState(null);
    const [swiper, setSwiper] = useState(null);
    const [swiperIsEnd, setSwiperIsEnd] = useState(null);
    const [swiperIsStart, setSwiperIsStart] = useState(null);
    const [activeIndex, setActiveIndex] = useState(0);

    // Methods
    const updateSwiperState = (swpr) => {
        setSwiper(swpr);
        setSwiperIsEnd(swpr.isEnd);
        setSwiperIsStart(swpr.isBeginning);
        setActiveIndex(swpr.activeIndex);
    };

    // Hooks
    useEffect(() => {
        setConfig({
            onResize: updateSwiperState,
            onSwiper: updateSwiperState,
            onSlideChange: updateSwiperState,
            onTransitionEnd: updateSwiperState,
            wrapperTag: 'div',
            ...swiperConfig
        });
    }, [swiperConfig]);

    // Render
    return (
        <CarouselContext.Provider value={useMemo(() => {
            return {
                config,
                swiper,
                swiperIsEnd,
                swiperIsStart,
                activeIndex
            };
        }, [
            config,
            swiper,
            swiperIsEnd,
            swiperIsStart,
            activeIndex
        ])}>
            {children}
        </CarouselContext.Provider>
    );
};


/**
 * @type {import('$types/components/partials/base.d.ts').CarouselWrapper}
 */
const CarouselWrapper = ({ children, className }) => {
    // Contexts
    const cc = useContext(CarouselContext);

    // Context Check
    if (cc === undefined) {
        throw new Error('CarouselWrapper must be used within a CarouselContext.Provider');
    }
    // Render
    return (
        <Swiper
            {...cc.config}
            className={className}
            key={cc.config}>
            {children}
        </Swiper>
    );
};

/**
 * @type {import('$types/components/partials/base.d.ts').CarouselNavigation}
 */

const CarouselNavigation = ({ look = 'row', className, floorplan }) => {
    // Contexts
    const cc = useContext(CarouselContext);

    // Context Check
    if (cc === undefined) {
        throw new Error('CarouselNavigation must be used within a CarouselContext.Provider');
    }

    let containerClass = '';
    let buttonClass = '';
    switch (look) {
        default:
            containerClass += 'flex';
            buttonClass += ' mr3';
            break;
        case 'col':
            containerClass += 'flex-col';
            buttonClass += ' mb3';
            break;
    }

    // Render
    return (
        <div
            className={`${containerClass} ${className ? className : ''}`}
            role='group'>
            <Button
                aria-label='carousel navigation left'
                className={`${floorplan ? 'bg-black white' : 'bg-white black'} p3 md-p5${buttonClass}`}
                disabled={cc.swiperIsStart}
                onClick={() => cc.swiper?.slidePrev()}
                renderIcon={<ArrowLeftIcon />}
                type='button' />
            <Button
                aria-label='carousel navigation right'
                className={`${floorplan ? 'bg-black white' : 'bg-white black'} p3 md-p5`}
                disabled={cc.swiperIsEnd}
                onClick={() => cc.swiper?.slideNext()}
                renderIcon={<ArrowRightIcon />}
                type='button' />
        </div>
    );
};

/**
 * @type {import('$types/components/partials/base.d.ts').CarouselPagination}
 */

const CarouselPagination = ({ className }) => {
    // Contexts
    const cc = useContext(CarouselContext);

    // Context Check
    if (cc === undefined) {
        throw new Error('CarouselPagination must be used within a CarouselContext.Provider');
    }

    // Render
    return (
        <div
            aria-label='carousel pagination'
            className={`${className ? className : ''}`}
            role='group'>
            {cc.swiper?.slides?.map((slide, idx) => {
                return (
                    <div
                        aria-label={`Go to slide ${idx}`}
                        className={`dot mr2 cursor-pointer${cc.swiper?.realIndex === idx ? ' active' : ''}`}
                        key={`swiper-pagination-${idx}`}
                        onClick={(e) => {
                            e.preventDefault();
                            cc.swiper?.slideTo(idx);
                        }
                        }/>);
            })}
        </div>
    );
};


const CarouselSlide = SwiperSlide;


/* ========================================================================= */
/* Exports */
/* ========================================================================= */

CarouselContext.displayName = 'CarouselContext';

export {
    Carousel,
    CarouselWrapper,
    CarouselSlide,
    CarouselNavigation,
    CarouselPagination,
    CarouselContext
};
